// React.lazy with a working retry (FR-043; research R5). React.lazy caches a rejected load for good,
// so after a failed chunk download "Try again" would fail again from the cache. retry() swaps in a
// fresh lazy component (which calls the factory again) and re-renders every mounted instance.
import {
  type ComponentType,
  lazy,
  type LazyExoticComponent,
  useSyncExternalStore,
} from "react";

type Factory<P> = () => Promise<{ default: ComponentType<P> }>;

export type LazyWithRetry<P> = {
  /** Render inside <Suspense> and an error boundary. */
  Component: ComponentType<P>;
  /** Call before resetting the error boundary: the next render loads the module again. */
  retry: () => void;
};

export function lazyWithRetry<P extends object>(
  factory: Factory<P>,
): LazyWithRetry<P> {
  let current: LazyExoticComponent<ComponentType<P>> = lazy(factory);
  let version = 0;
  const listeners = new Set<() => void>();
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  const getVersion = () => version;
  const getCurrent = () => current;

  function LazyComponent(props: P) {
    useSyncExternalStore(subscribe, getVersion, getVersion);
    const Current = getCurrent() as ComponentType<P>;
    return <Current {...props} />;
  }

  return {
    Component: LazyComponent,
    retry() {
      current = lazy(factory);
      version += 1;
      listeners.forEach((l) => l());
    },
  };
}
